import { tokens } from '@neo4j-ndl/base';
import { useNeedleTheme } from '@neo4j-ndl/react';
import type { NVL, Node, Relationship } from '@neo4j-nvl/base';
import { InteractiveNvlWrapper } from '@neo4j-nvl/react';
import { AURA_CONSOLE_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { ProfilingInfo } from '@nx/state';
import {
  ACTION,
  DEFAULT_RELATIONSHIP_COLOR,
  ONBOARDING_EXPERIENCE,
  TIER,
  consoleApi,
  getApiErrorMessage,
  useActiveOrg,
  useActiveProject,
  useAuth,
  useNotificationActions,
  usePermissions,
  useTrackEvent,
  useUnsafeAppContext,
} from '@nx/state';
import type { Validation } from '@nx/stdlib';
import { Objects, isNonEmptyString, isNotNullish, isNullish, validateYup } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import {
  constructNodes,
  constructRels,
  determineInstanceType,
  determineMarketingPreferenceFromRegionalTier,
  freeDefaults,
  isMarketplaceProject,
  proTrialDefaults,
  profilingDefaults,
} from './entities/helpers';
import type { InstanceFormData, OnboardingData } from './entities/model';
import { JOB_ROLE, ONBOARDING_INTENT, USE_CASE } from './entities/model';
import { CreateInstanceSection, IntentSection, RoleSection, UseCaseSection, WelcomeSection } from './sections';

const logger = createLogger(APP_SCOPE.aura);

export const schema = yup.object({
  companyName: yup.string().required().label('Company / Institution').min(2).max(255),
  firstName: yup.string().required().label('First (Given) name').min(1).max(50),
  lastName: yup.string().required().label('Last (Family) name').min(1).max(50),
});

export const validate = (data: OnboardingData, { onlyRequired }: { onlyRequired?: boolean } = {}) => {
  return validateYup(schema, data, onlyRequired ?? false);
};

export const OnboardingPage = () => {
  const [validationError, setValidationErrors] = useState<Validation<OnboardingData>>({});
  const { theme } = useNeedleTheme();
  const { bg, border } = tokens.theme[theme].palette.neutral;
  const trackEvent = useTrackEvent();
  const navigate = useNavigate();
  const { addNotification } = useNotificationActions();
  const [step, setStep] = useState(0);
  const auth = useAuth();
  const { isAllowed } = usePermissions();
  const [profilingData, setProfilingData] = useState(
    profilingDefaults({
      firstName: auth.user?.profile.given_name ?? '',
      lastName: auth.user?.profile.family_name ?? '',
    }),
  );

  const [createInstance, createInstanceRes] = consoleApi.useCreateInstanceMutation();
  // For Multi-db
  const [createAuraInstance, createAuraInstanceRes] = consoleApi.useCreateAuraInstanceMutation();

  const ref = useRef<NVL>(null);
  const project = useActiveProject();
  const org = useActiveOrg();
  const { data: user } = consoleApi.useGetUserDetailsQuery();
  const [updateProfiling, updateProfilingRes] = consoleApi.useUpdateProfilingInfoMutation();

  const [marketingPreference, setMarketingPreference] = useState(false);

  const [graph, setGraph] = useState<{ nodes: Node[]; rels: Relationship[] }>({
    nodes: constructNodes(profilingData),
    rels: [],
  });

  const instanceType = useMemo(
    () => determineInstanceType(user, profilingData.onboardingIntent, profilingData.jobRole),

    [profilingData.onboardingIntent, profilingData.jobRole, user],
  );
  const tierConfig = useMemo(() => {
    // Marketplace check here just to solve the problem with unexpected error thrown
    if (
      (instanceType === ONBOARDING_EXPERIENCE.Trial || isMarketplaceProject(project.projectType)) &&
      isNotNullish(project.tierConfigs.professional)
    ) {
      return project.tierConfigs.professional;
    }

    if (instanceType === ONBOARDING_EXPERIENCE.Free && isNotNullish(project.tierConfigs.free)) {
      return project.tierConfigs.free;
    }

    throw new Error('Tier is not supported');
  }, [instanceType, project.tierConfigs.free, project.tierConfigs.professional, project.projectType]);
  const [instanceData, setInstanceData] = useState<InstanceFormData>(
    instanceType === ONBOARDING_EXPERIENCE.Trial ? proTrialDefaults(tierConfig) : freeDefaults(tierConfig),
  );

  const handleZoomToFit = () => {
    ref.current?.fit(
      graph.nodes.map((node) => node.id),
      {},
    );
  };

  const handleProfilingDataChange = (newData: Partial<OnboardingData>) => {
    const key = Objects.keys(newData).find((value) => isNotNullish(value));
    const d = { ...profilingData, ...newData };
    if (isNotNullish(key)) {
      const validation = validate(d)?.[key];
      setValidationErrors((oldValues) => ({
        ...oldValues,
        [key]: validation,
      }));
    }
    setProfilingData((oldData) => ({ ...oldData, ...newData }));
    const nodes = constructNodes(d);
    const rels = constructRels(nodes);

    setGraph({
      nodes,
      rels,
    });
  };

  const handleInstanceDataChange = (newData: Partial<InstanceFormData>) => {
    setInstanceData((oldData) => ({ ...oldData, ...newData }));
  };

  useEffect(() => {
    handleZoomToFit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.rels]);

  const handleSubmitOnboardingData = (data: Partial<ProfilingInfo>, onSuccess: () => void, onFail?: () => void) => {
    if (Object.values(validationError).some((value) => isNotNullish(value))) {
      return;
    }
    updateProfiling({ ...data, organizationId: org.id })
      .unwrap()
      .then(() => {
        onSuccess();
      })
      .catch((error: FetchBaseQueryError | SerializedError) => {
        onFail?.();
        const errorMessage = getApiErrorMessage(error);
        addNotification({ type: 'danger', title: 'Error', description: errorMessage, timeout: 5000 });
        logger.error(errorMessage);
      });
  };

  const handleCreate = () => {
    if (isNullish(instanceData)) {
      return;
    }
    if (isNullish(instanceData.region)) {
      return;
    }
    const multiDbFree = project.capabilities.multi_db_free && instanceData.tier === TIER.FREE;
    const createMutation = multiDbFree ? createAuraInstance : createInstance;

    createMutation({
      name: instanceData.name,
      cloudProvider: instanceData.cloudProvider,
      memory: instanceData.memory,
      storage: instanceData.storage,
      region: instanceData.region,
      version: instanceData.version,
      tier: instanceData.tier,
      projectId: project.id,
      organizationId: org.id,
      ...(instanceType === ONBOARDING_EXPERIENCE.Trial && {
        isTrial: true,
      }),
    })
      .unwrap()
      .then((res) => {
        const event = {
          tier: instanceData.tier,
          ...(instanceType === ONBOARDING_EXPERIENCE.Trial && {
            proTrial: 'proTrial_included',
          }),
        };
        trackEvent({ event: AURA_CONSOLE_EVENTS.INSTANCE_CREATE, properties: event, scope: APP_SCOPE.aura });
        void navigate('/instance-created', { state: { createdInstance: res } });
      })
      .catch((error: FetchBaseQueryError | SerializedError) => {
        const errorMessage = getApiErrorMessage(error);
        addNotification({ type: 'danger', title: 'Error', description: errorMessage, timeout: 5000 });
      });
  };

  return (
    <div
      className="bg-neutral-bg-weak border-neutral-border-weak m-6 flex w-full justify-center rounded-[48px] border p-6"
      data-testid="onboarding-layout"
    >
      {step === 0 && (
        <WelcomeSection
          data={profilingData}
          onDataChange={handleProfilingDataChange}
          onNextStep={() => {
            handleSubmitOnboardingData(
              {
                firstName: profilingData.firstName.trim(),
                lastName: profilingData.lastName.trim(),
                companyName: profilingData.companyName.trim(),
                ...(isNotNullish(user?.marketingChoiceRequired) && {
                  marketingPreference: determineMarketingPreferenceFromRegionalTier(
                    user.marketingChoiceRequired,
                    marketingPreference,
                  ),
                }),
              },
              () => {
                setStep(1);
              },
            );
          }}
          isLoading={updateProfilingRes.isLoading}
          regionalTier={user?.marketingChoiceRequired}
          marketingPreference={marketingPreference}
          onMarketingPreferenceChange={() => setMarketingPreference(!marketingPreference)}
          validation={validationError}
        />
      )}
      {step === 1 && (
        <IntentSection
          onNextStep={(onboardingIntent: ONBOARDING_INTENT, onboardingIntentOther?: string) => {
            const submitData = {
              onboardingIntent,
              onboardingIntentOther,
            };

            handleProfilingDataChange(submitData);
            if (onboardingIntent !== ONBOARDING_INTENT.Other || isNonEmptyString(onboardingIntentOther)) {
              handleSubmitOnboardingData(
                { onboardingIntent },
                () => {
                  setStep(2);
                },
                () => {
                  handleProfilingDataChange({ onboardingIntent: null, onboardingIntentOther: null });
                },
              );
            }
          }}
          onBack={() => {
            handleProfilingDataChange({ onboardingIntent: null, onboardingIntentOther: null });
            setStep(0);
          }}
          onDataChange={handleProfilingDataChange}
          isLoading={updateProfilingRes.isLoading}
          data={profilingData}
        />
      )}
      {step === 2 && (
        <RoleSection
          onNextStep={(jobRole: JOB_ROLE) => {
            handleProfilingDataChange({ jobRole });
            handleSubmitOnboardingData(
              { jobRole: jobRole.valueOf() },
              () => {
                setStep(3);
              },
              () => {
                handleProfilingDataChange({ jobRole: null, jobRoleOther: null });
              },
            );
          }}
          onBack={() => {
            handleProfilingDataChange({ onboardingIntent: null, onboardingIntentOther: null });
            setStep(1);
          }}
          isLoading={updateProfilingRes.isLoading}
          data={profilingData}
        />
      )}
      {step === 3 && (
        <UseCaseSection
          onNextStep={(primaryUseCase: USE_CASE, primaryUseCaseOther?: string) => {
            const submitData = {
              primaryUseCase,
              primaryUseCaseOther,
            };

            handleProfilingDataChange(submitData);
            if (primaryUseCase !== USE_CASE.Other || isNonEmptyString(primaryUseCaseOther)) {
              handleSubmitOnboardingData(
                submitData,
                () => {
                  if (
                    isMarketplaceProject(project.projectType) ||
                    isNullish(project.tierConfigs.free) ||
                    !project.availableActions.create_pro_trial.enabled
                  ) {
                    void navigate(`/projects/${project.id}/instances`, {
                      state: {
                        openInstanceModal: isAllowed(ACTION.CREATE, `namespaces/${project.id}/databases`),
                      },
                    });
                  } else {
                    setStep(4);
                    setInstanceData(
                      instanceType === ONBOARDING_EXPERIENCE.Trial
                        ? proTrialDefaults(tierConfig)
                        : freeDefaults(tierConfig),
                    );
                  }
                },
                () => {
                  handleProfilingDataChange({ primaryUseCase: null, primaryUseCaseOther: null });
                },
              );
            }
          }}
          onBack={() => {
            handleProfilingDataChange({
              jobRole: null,
              jobRoleOther: null,
              primaryUseCase: null,
              primaryUseCaseOther: null,
            });
            setStep(2);
          }}
          isLoading={updateProfilingRes.isLoading}
          data={profilingData}
          onDataChange={handleProfilingDataChange}
        />
      )}

      {step === 4 && (
        <CreateInstanceSection
          instanceType={instanceType}
          data={instanceData}
          isLoading={createInstanceRes.isLoading || createAuraInstanceRes.isLoading}
          onDataChange={handleInstanceDataChange}
          tierConfig={tierConfig}
          onCreate={handleCreate}
          showSkipLink={
            (isNotNullish(profilingData.jobRole) &&
              [JOB_ROLE.DataAnalyst, JOB_ROLE.DataScientist].includes(profilingData.jobRole)) ||
            (isNotNullish(profilingData.onboardingIntent) &&
              profilingData.onboardingIntent === ONBOARDING_INTENT.GraphAnalytics)
          }
        />
      )}
      <div className="bg-neutral-bg-default hidden h-full w-full rounded-[24px] sm:block">
        <InteractiveNvlWrapper
          ref={ref}
          nodes={graph.nodes}
          rels={graph.rels}
          layoutOptions={{
            enableCytoscape: false,
          }}
          nvlOptions={{
            minZoom: 1,
            maxZoom: 3,
            relationshipThreshold: 0.55,
            disableTelemetry: true,
            styling: {
              selectedInnerBorderColor: bg.default,
              defaultRelationshipColor: DEFAULT_RELATIONSHIP_COLOR,
              dropShadowColor: border.weak,
            },
          }}
          layout="forceDirected"
          mouseEventCallbacks={{
            onHover: true,
            onZoom: false,
          }}
        />
      </div>
    </div>
  );
};

export const GuardedOnboardingPage = () => {
  const org = useActiveOrg();
  const project = useActiveProject();
  const { userHomePath } = useUnsafeAppContext();
  const navigate = useNavigate();

  // Navigate the user back to the home page if they either have completed the onboarding or if the feature toggle is
  // disabled.
  if (org.onboardingCompleted || !org.featureToggles['plg-new-onboarding']) {
    const homePageType = org.featureToggles['plg-home-page-types'];
    const path =
      homePageType !== 'none' ? `/projects/${project.id}/home` : (userHomePath ?? `/projects/${project.id}/instances`);
    void navigate(path, { replace: true });
    return null;
  }

  return <OnboardingPage />;
};
